/**
 * Licence state — what the admin and the storefront are told about the
 * deployment's licence.
 *
 * Admin: the member read of `platform/licence` and the bootstrap's
 * `licenceRequired` and project id, derived through
 * `Licence.deriveLicenceState`. The browser checks no signature: the banner
 * and the Settings line inform, and the callables enforce. An absent or
 * undecodable record on a licence-requiring deployment reads read-only; a
 * deployment that needs no licence shows nothing.
 *
 * Storefront: whether ticket sales are paused, from the page shell's
 * `<meta name="licence-state">`. An absent tag is unknown, never paused.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { Licence } from "@momo/types";

export const licenceBootstrap = state<{
	licenceRequired: boolean;
	projectId: string;
}>("licence/bootstrap").memory({ licenceRequired: false, projectId: "" });

export const platformLicence = state<
	| { status: "unanswered" }
	| { status: "failed" }
	| { status: "answered"; record: Licence.PlatformLicence | null }
>("licence/platform-licence").memory({ status: "unanswered" });

/** The instant the derivation reads, advanced by the admin's `.sync` on a
 *  coarse clock so an open tab crosses into grace and read-only. */
export const licenceNow = state<number>("licence/now").memory(Date.now());

export const salesPaused = state<boolean>("licence/sales-paused").memory(false);

const DAY_MS = 24 * 60 * 60 * 1000;

/** Null while there is nothing to say: unanswered, failed, or no licence
 *  required. A licence signed for another project has no end date of this
 *  deployment's, so it carries none. */
export const adminLicence = computed(() => {
	const read = platformLicence.value;
	const { licenceRequired, projectId } = licenceBootstrap.value;
	if (read.status !== "answered" || !licenceRequired) return null;
	let payload: Licence.LicencePayload | null = null;
	try {
		payload =
			Licence.LicencePayloadSchema.safeParse(
				JSON.parse(
					atob(
						(read.record?.token.split(".")[0] ?? "")
							.replace(/-/g, "+")
							.replace(/_/g, "/"),
					),
				),
			).data ?? null;
	} catch {
		// Not base64 or not JSON: unreadable derives read-only, as missing does.
	}
	const own = payload?.projectId === projectId ? payload : null;
	return {
		state: Licence.deriveLicenceState(own, new Date(licenceNow.value), {
			licenceRequired,
			projectId,
		}),
		endsAt: own?.endsAt ?? null,
		salesStopAt: own
			? new Date(Date.parse(own.endsAt) + own.graceDays * DAY_MS).toISOString()
			: null,
	};
});
